import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Upload } from 'lucide-react';
import { api } from '../../services/api';
import SignaturePad from './SignaturePad';

type SignatureManagerProps = {
  /** Mode for signature management */
  mode: 'user' | 'inspection' | 'contact';
  /** ID of the entity (inspection_id or contact_id) - not needed for user mode */
  entityId?: string;
  /** Callback when signature is saved */
  onSaved?: () => void;
  /** Additional class name */
  className?: string;
};

type SignatureResponse = {
  signature_url: string | null;
  updated_at: string | null;
  has_signature: boolean;
};

export function SignatureManager({
  mode,
  entityId,
  onSaved,
  className = '',
}: SignatureManagerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  // Determine API endpoints based on mode
  const getEndpoint = () => {
    switch (mode) {
      case 'user':
        return '/users/me/signature';
      case 'inspection':
        return `/inspections/${entityId}/signature`;
      case 'contact':
        return `/contacts/${entityId}/signature`;
    }
  };

  const queryKey = ['signature', mode, entityId];

  // Fetch current signature
  const { data: signature, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await api.get<SignatureResponse>(getEndpoint());
      return response.data;
    },
    enabled: mode === 'user' || !!entityId,
  });

  // Upload signature mutation
  const uploadMutation = useMutation({
    mutationFn: async (signatureData: string) => {
      const response = await api.post(getEndpoint(), {
        signature_data: signatureData,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setIsEditing(false);
      onSaved?.();
    },
  });

  // Delete signature mutation (only for user mode)
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete('/users/me/signature');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Use saved signature for inspection
  const useSavedMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(
        `/inspections/${entityId}/use-saved-signature`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      onSaved?.();
    },
  });

  const handleSave = (signatureData: string) => {
    uploadMutation.mutate(signatureData);
  };

  // Upload a signature image file instead of drawing it (Shane: "it only lets
  // you sign it, not take a file" — #292). The endpoint takes a data URL, so a
  // file's data URL flows through the exact same save path as a drawn one.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState('');
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-picked later
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFileError('Please choose an image file (PNG or JPG).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFileError('Image is too large (max 5 MB).');
      return;
    }
    setFileError('');
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string')
        uploadMutation.mutate(reader.result);
    };
    reader.onerror = () => setFileError('Could not read that file.');
    reader.readAsDataURL(file);
  };

  if (isLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-40 bg-slate-200 dark:bg-slate-700 rounded-lg" />
      </div>
    );
  }

  // Show signature pad when editing
  if (isEditing) {
    return (
      <div className={className}>
        <SignaturePad
          onSave={handleSave}
          onCancel={() => setIsEditing(false)}
          width={400}
          height={100}
          disabled={uploadMutation.isPending}
        />
        {uploadMutation.isError && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            Failed to save signature. Please try again.
          </p>
        )}
      </div>
    );
  }

  // Show existing signature or empty state
  return (
    <div className={className}>
      {signature?.has_signature ? (
        <div className="space-y-3">
          {/* Signature preview */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-900">
            <img
              src={signature.signature_url || ''}
              alt="Signature"
              className="max-h-24 mx-auto"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Update
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
            />

            {mode === 'user' && (
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Remove
              </button>
            )}
          </div>

          {signature.updated_at && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Last updated:{' '}
              {new Date(signature.updated_at).toLocaleDateString()}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Empty state */}
          <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center">
            <Upload className="w-8 h-8 mx-auto text-slate-400 dark:text-slate-500 mb-2" />
            <p className="text-sm text-slate-600 dark:text-slate-400">
              No signature on file
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded-md transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Draw Signature
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md transition-colors disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              Upload Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
            />

            {/* For inspection mode, offer to use saved signature */}
            {mode === 'inspection' && (
              <button
                type="button"
                onClick={() => useSavedMutation.mutate()}
                disabled={useSavedMutation.isPending}
                className="flex items-center gap-1.5 px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md transition-colors disabled:opacity-50"
              >
                Use Saved Signature
              </button>
            )}
          </div>

          {useSavedMutation.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              No saved signature found. Please draw a new one.
            </p>
          )}
          {fileError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {fileError}
            </p>
          )}
          {uploadMutation.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              Failed to save signature. Please try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default SignatureManager;
